<template>
  <b-modal
    v-if="offer"
    id="modal-offer-chain"
    modal-class="modal-offer-chain"
    :hide-header="true"
    :hide-footer="true"
    :no-close-on-backdrop="false"
    :no-fade="false"
    @show="onShow"
    @hidden="onHidden"
  >
    <template slot="default">
      <!-- Preload the heavy background; drives the loading spinner. Kept outside
           the canvas so its @load still fires while the canvas is hidden. -->
      <img
        v-if="chainOffer && chainOffer.image_path_full"
        ref="bgPreload"
        :src="chainOffer.image_path_full"
        style="display: none"
        @load="onBgLoad"
        @error="onBgError"
      />

      <Spinner
        v-if="loading"
        size="medium"
        context="Loading Offer..."
        classes="dead-center"
      ></Spinner>

      <div v-if="!loading" class="chain-canvas" :style="offerBackgroundStyle">
        <!-- Title logo: top-center (baked image from chain config) -->
        <img
          v-if="chainLogo"
          :src="chainLogo"
          class="chain-title-logo"
          alt=""
        />

        <!-- Countdown: top-left -->
        <div v-if="endDate" class="chain-duration" :style="timerStyle">
          <div class="icon-hourglass"></div>
          <countdown-digital
            :time="endDate"
            :with-time-diff="true"
            :hide-minutes="true"
            @over="onChainOver"
          ></countdown-digital>
        </div>

        <button class="offer-close-btn" @click="closeModal">
          <span>&times;</span>
        </button>

        <offer-layout-chain
          v-if="chainOffer && chainOffer.packages && chainOffer.packages.length"
          :offer="chainOffer"
          :display-config="displayConfig"
          @purchase="handlePurchase"
          @locked="onLockedClick"
        />

        <!-- Inline message when a locked tile is clicked -->
        <div v-if="lockedMessage" class="chain-locked-msg">
          {{ lockedMessage }}
        </div>
      </div>
    </template>
  </b-modal>
</template>

<script>
import OfferLayoutChain from "./offer-layout-chain.vue";
import CountdownDigital from "@/components/base/countdown-digital";
import AnimationFullRewardEarned from "@/components/animation/animation-full-reward-earned";
import Spinner from "@/components/animation/animation-spinner.vue";
import { chainOfferData } from "./mixins/chainOfferData";
import { chainRewards } from "./mixins/chainRewards";
import { chainPurchase } from "./mixins/chainPurchase";
import { chainClaim } from "./mixins/chainClaim";
import { freeClaim } from "../mixins/freeClaim";

export default {
  name: "ModalOfferChain",
  components: {
    OfferLayoutChain,
    CountdownDigital,
    Spinner,
  },
  mixins: [
    AnimationFullRewardEarned,
    chainOfferData,
    chainRewards,
    chainPurchase,
    freeClaim,
    chainClaim,
  ],
  props: {
    offerData: {
      type: Object,
      required: true,
      default: null,
    },
  },
  data() {
    return {
      loading: true,
      lockedMessage: "",
      lockedTimer: null,
      // Explainer-first gating (see onShow): block the chain until the
      // explainer closes, and stop concurrent triggers from racing it open.
      chainAllowed: false,
      explainerPending: false,
      // Chain id to remove once the modal has finished hiding (set when the
      // in-modal countdown expires) — deferred so we don't unmount mid-hide.
      pendingRemoveId: null,
    };
  },
  watch: {
    offer(newVal) {
      if (newVal) {
        this.openModal();
      }
    },
  },
  mounted() {
    if (this.offer) {
      this.openModal();
    }
  },
  beforeDestroy() {
    clearTimeout(this.lockedTimer);
  },
  methods: {
    onShow(bvEvent) {
      if (this.chainAllowed) return;
      bvEvent.preventDefault();
      if (this.explainerPending) return;
      this.explainerPending = true;
      this.$eventBus.$emit("explainer:show", {
        feature: "offerchain",
        firstOpen: true,
        title: this.chainTitle,
        onClose: () => {
          this.chainAllowed = true;
          this.explainerPending = false;
          this.openModal();
        },
      });
    },
    openModal() {
      // Show the spinner until the background image loads. If there's no
      // background, there's nothing to wait for.
      const hasImage = !!(this.chainOffer && this.chainOffer.image_path_full);
      this.loading = hasImage;
      this.$nextTick(() => {
        this.$bvModal.show("modal-offer-chain");
        // The preload <img> only fires @load when its src actually (re)loads.
        // On a refetch/reopen the URL is unchanged, so a cached image never
        // fires @load again — detect that here so the spinner doesn't hang.
        if (hasImage) {
          const img = this.$refs.bgPreload;
          if (img && img.complete) {
            this.onBgLoad();
          }
        }
      });
    },
    // Locked tile clicked: show a transient inline hint.
    onLockedClick() {
      this.lockedMessage = this.$t("pay.offer.chain.unlock.previous");
      clearTimeout(this.lockedTimer);
      this.lockedTimer = setTimeout(() => {
        this.lockedMessage = "";
      }, 2500);
    },
    closeModal() {
      this.$bvModal.hide("modal-offer-chain");
    },
    onHidden() {
      this.chainAllowed = false;
      this.explainerPending = false;
      // Now that the modal is fully hidden it's safe to mutate state that the
      // modal's v-if depends on. Remove an expired chain, else drop a stale
      // selection (its last package was just collected).
      if (this.pendingRemoveId != null) {
        this.$store.commit("shop/removeOfferChain", this.pendingRemoveId);
        this.pendingRemoveId = null;
      } else {
        const id = this.offerData && this.offerData.id;
        const active = this.$store.getters["shop/getOfferChains"] || [];
        if (id != null && !active.some((c) => c && c.id === id)) {
          this.$store.commit("shop/setOfferChainData", null);
        }
      }
      this.$emit("hidden");
    },
    // Chain expired: close the modal first, then remove this chain on @hidden
    // (deferred so we don't unmount the modal mid-hide-transition).
    onChainOver() {
      this.pendingRemoveId = (this.offerData && this.offerData.id) ?? null;
      this.closeModal();
    },
  },
};
</script>

<style lang="scss">
// Sized to match the season-pass modal (contained frame), not the full-bleed
// dynamic offer modal.
$modal-width: 1121px;
$modal-height: 715px;
$canvas-height: 630px;

.modal-offer-chain {
  .modal-dialog {
    max-width: $modal-width;
    width: 100%;
    height: $modal-height;
    padding-top: 50px;

    @media (max-height: 820px), (max-width: 1250px) {
      transform: scale(0.9) !important;
      top: -50px;
    }
    @media (max-height: 730px), (max-width: 1100px) {
      transform: scale(0.8) !important;
      top: -130px;
      left: -30px;
    }
  }

  .modal-content {
    width: 100%;
    height: 100%;
    background: none;
    color: white;
    text-align: center;
    z-index: 0;
    border: none;
    border-radius: 0;
  }

  .modal-body {
    position: relative;
    flex: 0 0 $canvas-height !important;
    width: $modal-width;
    height: $canvas-height;
    padding: 0;
    margin-top: 0;
    overflow: hidden;
  }
}
</style>

<style scoped lang="scss">
// Inline hint shown when a locked tile is clicked.
.chain-locked-msg {
  position: absolute;
  left: 50%;
  bottom: 28px;
  transform: translateX(-50%);
  z-index: 20;
  padding: 10px 22px;
  border-radius: 22px;
  background: rgba(0, 0, 0, 0.75);
  color: #fff;
  font-size: 18px;
  font-weight: bold;
  white-space: nowrap;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.7);
  pointer-events: none;
}

.chain-canvas {
  position: relative;
  width: 100%;
  height: 100%;
  // Frame color is ACP-driven (display_config.style["border-color"]); falls
  // back to the original gold. box-sizing keeps the border inside the canvas.
  border: 5px solid var(--chain-border-color, rgb(250 188 37));
  box-sizing: border-box;
  background-position: center;
  background-repeat: no-repeat;
  // Crop to fill the 16:9 canvas instead of stretching (source is 1920×1080).
  background-size: cover;
  overflow: hidden;
}

// Title logo: top-center, between the top-left countdown and top-right close.
.chain-title-logo {
  position: absolute;
  top: 14px;
  left: 50%;
  transform: translateX(-50%);
  height: 72px;
  width: auto;
  max-width: 55%;
  object-fit: contain;
  z-index: 10;
  pointer-events: none;
}

// Countdown: top-left.
.chain-duration {
  position: absolute;
  top: 22px;
  left: 24px;
  z-index: 10;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  // Both icon + text default to their original colors (icon light-blue, text
  // white) and switch to display_config.style["timer-color"] when set.
  color: var(--timer-color, #fff);
  opacity: 0.9;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.7);

  .icon-hourglass {
    width: 19px;
    height: 25px;
    // Masked (not background) so the fill color is CSS-driven.
    background-color: var(--timer-color, #4de5ff);
    -webkit-mask: url(~@/assets/img/icons/hourglass.svg) center/100% 100%
      no-repeat;
    mask: url(~@/assets/img/icons/hourglass.svg) center/100% 100% no-repeat;
  }

  ::v-deep .countdown-digital {
    color: var(--timer-color, #fff);
  }
}

.offer-close-btn {
  position: absolute;
  top: 12px;
  right: 14px;
  width: 30px;
  height: 30px;
  background: rgba(0, 0, 0, 0.5);
  border: 2px solid rgba(255, 255, 255, 0.8);
  border-radius: 50%;
  cursor: pointer;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;

  span {
    color: white;
    font-size: 28px;
    line-height: 0;
    font-weight: bold;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-top: -3px;
  }

  &:hover {
    background: rgba(0, 0, 0, 0.7);
    border-color: white;
    transform: scale(1.1);
  }

  &:focus {
    outline: none;
  }
}
</style>
